import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Checkbox, Drawer, Input, Select, Slider, Space } from "antd";
import BufferedNumberInput from "./BufferedNumberInput";
import {
  DEFAULT_BACKGROUND_OPACITY,
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_FONT_SIZE,
  DEFAULT_GROUP_BACKGROUND_HEIGHT,
  DEFAULT_GROUP_BACKGROUND_WIDTH,
  DEFAULT_ROTATION,
  DEFAULT_SYNC_ROTATIONS,
  MIN_SHAPE_SIZE,
} from "./constants";
import type { GroupEditorDrawerProps, Waypoint } from "./types";

function GroupEditorDrawer({
  open,
  onClose,
  onSave,
  onDelete,
  loading = false,
  groupWaypoint,
  mode,
  isPinMode,
  linkedBankLockMap = {},
  onGroupWaypointChange,
  onGroupBankSelectionChange,
  onAdditionalLiftSelectionChange,
  onManagedLiftChange,
  onTogglePinMode,
  availableBankOptions,
  availableAdditionalLiftOptions,
  managedLifts,
}: GroupEditorDrawerProps) {
  const [showViewOptions, setShowViewOptions] = useState<boolean>(false);
  const backgroundRotationDeg = groupWaypoint.backgroundRotationDeg ?? DEFAULT_ROTATION;
  const nameRotationDeg = groupWaypoint.nameRotationDeg ?? DEFAULT_ROTATION;
  const syncRotations = groupWaypoint.syncRotations ?? DEFAULT_SYNC_ROTATIONS;
  const isLinkedBankLocked = (bankName?: string) =>
    Boolean(bankName && linkedBankLockMap[bankName] === true);
  const lockGroupAspectRatio = managedLifts.some((lift) => isLinkedBankLocked(lift.linkedBank));

  const getGroupSizeLockedToWidth = (nextWidth: number) => {
    const currentWidth = Math.max(groupWaypoint.backgroundWidth, MIN_SHAPE_SIZE);
    const currentHeight = Math.max(groupWaypoint.backgroundHeight, MIN_SHAPE_SIZE);
    const ratio = currentHeight / currentWidth;

    return {
      backgroundWidth: nextWidth,
      backgroundHeight: Math.max(MIN_SHAPE_SIZE, Math.round(nextWidth * ratio)),
    };
  };

  const getGroupSizeLockedToHeight = (nextHeight: number) => {
    const currentWidth = Math.max(groupWaypoint.backgroundWidth, MIN_SHAPE_SIZE);
    const currentHeight = Math.max(groupWaypoint.backgroundHeight, MIN_SHAPE_SIZE);
    const ratio = currentWidth / currentHeight;

    return {
      backgroundWidth: Math.max(MIN_SHAPE_SIZE, Math.round(nextHeight * ratio)),
      backgroundHeight: nextHeight,
    };
  };

  const getLiftSizeLockedToWidth = (lift: Waypoint, nextWidth: number) => {
    const currentWidth = Math.max(lift.backgroundWidth ?? MIN_SHAPE_SIZE, MIN_SHAPE_SIZE);
    const currentHeight = Math.max(lift.backgroundHeight ?? MIN_SHAPE_SIZE, MIN_SHAPE_SIZE);
    const ratio = currentHeight / currentWidth;

    return {
      backgroundWidth: nextWidth,
      backgroundHeight: Math.max(MIN_SHAPE_SIZE, Math.round(nextWidth * ratio)),
    };
  };

  const getLiftSizeLockedToHeight = (lift: Waypoint, nextHeight: number) => {
    const currentWidth = Math.max(lift.backgroundWidth ?? MIN_SHAPE_SIZE, MIN_SHAPE_SIZE);
    const currentHeight = Math.max(lift.backgroundHeight ?? MIN_SHAPE_SIZE, MIN_SHAPE_SIZE);
    const ratio = currentWidth / currentHeight;

    return {
      backgroundWidth: Math.max(MIN_SHAPE_SIZE, Math.round(nextHeight * ratio)),
      backgroundHeight: nextHeight,
    };
  };

  return (
    <Drawer
      title={mode === "edit" ? "Edit Lift Group" : "Add Group"}
      placement="right"
      onClose={onClose}
      open={open}
      mask={false}
      width={DEFAULT_DRAWER_WIDTH}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {mode === "edit" && onDelete && (
            <Button danger onClick={onDelete} disabled={loading}>
              Delete
            </Button>
          )}
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="primary" onClick={onSave} loading={loading} disabled={loading}>
            Save
          </Button>
        </div>
      }
    >
      <div style={{ pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.7 : 1 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 8 }}>Group Name</div>
            <Input
              placeholder="Lift group name"
              value={groupWaypoint.nickname}
              onChange={(event) => {
                const value = event.target.value;
                onGroupWaypointChange({
                  ...groupWaypoint,
                  nickname: value || undefined,
                });
              }}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Waypoint Anchor</div>
            <Space size="small" style={{ width: "100%", alignItems: "flex-end" }}>
              <div>
                <div style={{ marginBottom: 6 }}>x</div>
                <BufferedNumberInput
                  style={{ width: 92 }}
                  value={groupWaypoint.x}
                  fallback={0}
                  onCommit={(value) => {
                    onGroupWaypointChange({
                      ...groupWaypoint,
                      x: value,
                    });
                  }}
                  placeholder="x"
                />
              </div>
              <div>
                <div style={{ marginBottom: 6 }}>y</div>
                <BufferedNumberInput
                  style={{ width: 92 }}
                  value={groupWaypoint.y}
                  fallback={0}
                  onCommit={(value) => {
                    onGroupWaypointChange({
                      ...groupWaypoint,
                      y: value,
                    });
                  }}
                  placeholder="y"
                />
              </div>
              <Button type={isPinMode ? "primary" : "default"} onClick={onTogglePinMode}>
                Click to Pin
              </Button>
            </Space>

            <div style={{ marginTop: 10 }}>
              <Button
                type="primary"
                onClick={() => {
                  setShowViewOptions((previous) => !previous);
                }}
              >
                View Options
              </Button>
            </div>

            {showViewOptions && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  backgroundColor: "#fafafa",
                }}
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div>
                    <div style={{ marginBottom: 6 }}>Background Color</div>
                    <input
                      type="color"
                      value={groupWaypoint.backgroundColorHex}
                      onChange={(event) => {
                        onGroupWaypointChange({
                          ...groupWaypoint,
                          backgroundColorHex: event.target.value,
                        });
                      }}
                      style={{
                        width: 52,
                        height: 32,
                        border: "none",
                        padding: 0,
                        background: "none",
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ marginBottom: 6 }}>Transparency (%)</div>
                    <Slider
                      min={0}
                      max={100}
                      value={groupWaypoint.backgroundOpacity}
                      onChange={(value) => {
                        onGroupWaypointChange({
                          ...groupWaypoint,
                          backgroundOpacity:
                            typeof value === "number" ? value : DEFAULT_BACKGROUND_OPACITY,
                        });
                      }}
                      tooltip={{ formatter: (value) => `${value ?? 0}%` }}
                    />
                  </div>

                  <Space size="small" style={{ width: "100%" }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>Width (px)</div>
                      <BufferedNumberInput
                        min={MIN_SHAPE_SIZE}
                        value={groupWaypoint.backgroundWidth}
                        fallback={DEFAULT_GROUP_BACKGROUND_WIDTH}
                        onCommit={(value) => {
                          if (lockGroupAspectRatio) {
                            onGroupWaypointChange({
                              ...groupWaypoint,
                              ...getGroupSizeLockedToWidth(value),
                            });
                            return;
                          }

                          onGroupWaypointChange({
                            ...groupWaypoint,
                            backgroundWidth: value,
                          });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6 }}>Height (px)</div>
                      <BufferedNumberInput
                        min={MIN_SHAPE_SIZE}
                        value={groupWaypoint.backgroundHeight}
                        fallback={DEFAULT_GROUP_BACKGROUND_HEIGHT}
                        onCommit={(value) => {
                          if (lockGroupAspectRatio) {
                            onGroupWaypointChange({
                              ...groupWaypoint,
                              ...getGroupSizeLockedToHeight(value),
                            });
                            return;
                          }

                          onGroupWaypointChange({
                            ...groupWaypoint,
                            backgroundHeight: value,
                          });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                  </Space>

                  <div
                    style={{
                      marginTop: 8,
                      padding: 10,
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Rotations</div>

                    <div style={{ marginBottom: 10 }}>
                      <Checkbox
                        checked={syncRotations}
                        onChange={(event) => {
                          onGroupWaypointChange({
                            ...groupWaypoint,
                            syncRotations: event.target.checked,
                          });
                        }}
                      >
                        Keep All Rotations Synced
                      </Checkbox>
                    </div>

                    <Space size="small" style={{ width: "100%" }}>
                      <div>
                        <div style={{ marginBottom: 6 }}>Background (deg)</div>
                        <BufferedNumberInput
                          min={-360}
                          max={360}
                          value={backgroundRotationDeg}
                          fallback={DEFAULT_ROTATION}
                          onCommit={(nextRotation) => {
                            if (syncRotations) {
                              onGroupWaypointChange({
                                ...groupWaypoint,
                                backgroundRotationDeg: nextRotation,
                                nameRotationDeg: nextRotation,
                              });
                              return;
                            }

                            onGroupWaypointChange({
                              ...groupWaypoint,
                              backgroundRotationDeg: nextRotation,
                            });
                          }}
                          style={{ width: 130 }}
                        />
                      </div>

                      <div>
                        <div style={{ marginBottom: 6 }}>Name Header (deg)</div>
                        <BufferedNumberInput
                          min={-360}
                          max={360}
                          value={nameRotationDeg}
                          fallback={DEFAULT_ROTATION}
                          onCommit={(nextRotation) => {
                            if (syncRotations) {
                              onGroupWaypointChange({
                                ...groupWaypoint,
                                backgroundRotationDeg: nextRotation,
                                nameRotationDeg: nextRotation,
                              });
                              return;
                            }

                            onGroupWaypointChange({
                              ...groupWaypoint,
                              nameRotationDeg: nextRotation,
                            });
                          }}
                          style={{ width: 130 }}
                        />
                      </div>
                    </Space>
                  </div>

                  <div>
                    <Checkbox
                      checked={groupWaypoint.displayName}
                      onChange={(event) => {
                        onGroupWaypointChange({
                          ...groupWaypoint,
                          displayName: event.target.checked,
                        });
                      }}
                    >
                      Display Name
                    </Checkbox>
                  </div>

                  {groupWaypoint.displayName && (
                    <Space size="small" style={{ width: "100%", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ marginBottom: 6 }}>Font Size (px)</div>
                        <BufferedNumberInput
                          min={8}
                          max={48}
                          value={groupWaypoint.fontSize}
                          fallback={DEFAULT_FONT_SIZE}
                          onCommit={(value) => {
                            onGroupWaypointChange({
                              ...groupWaypoint,
                              fontSize: value,
                            });
                          }}
                          style={{ width: 110 }}
                        />
                      </div>
                      <div>
                        <div style={{ marginBottom: 6 }}>Font Color</div>
                        <input
                          type="color"
                          value={groupWaypoint.fontColorHex}
                          onChange={(event) => {
                            onGroupWaypointChange({
                              ...groupWaypoint,
                              fontColorHex: event.target.value,
                            });
                          }}
                          style={{
                            width: 52,
                            height: 24,
                            border: "none",
                            padding: 0,
                            background: "none",
                            display: "block",
                          }}
                        />
                      </div>
                    </Space>
                  )}
                </Space>
              </div>
            )}
          </div>

          <div>
            <div style={{ marginBottom: 8 }}>Attached Banks</div>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Select banks"
              value={
                Array.isArray(groupWaypoint.banks)
                  ? Array.from(
                      new Set(groupWaypoint.banks.map((bank) => bank.bankName).filter(Boolean)),
                    )
                  : []
              }
              options={availableBankOptions}
              onChange={(value) => {
                const bankNames = (value || []).filter(
                  (bankName): bankName is string =>
                    typeof bankName === "string" && bankName.trim().length > 0,
                );
                onGroupBankSelectionChange(bankNames);
              }}
            />
          </div>

          <div>
            <div style={{ marginBottom: 8 }}>Additional Lifts</div>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Select additional lifts"
              value={groupWaypoint.additionalLiftKeys || []}
              options={availableAdditionalLiftOptions}
              onChange={(value) => {
                onAdditionalLiftSelectionChange(value);
              }}
            />
          </div>

          {managedLifts.length > 0 && (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>Managed Lift View Options</div>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                {managedLifts.map((lift) => (
                  <div
                    key={lift.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: 10,
                      backgroundColor: "#fff",
                    }}
                  >
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>
                      {lift.nickname || lift.linkedLift || "Lift"}
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <div style={{ marginBottom: 6 }}>Linked Lift</div>
                      <Input value={lift.nickname || lift.linkedLift || ""} disabled />
                    </div>

                    <Space size="small" style={{ width: "100%", alignItems: "flex-end" }}>
                      <div>
                        <div style={{ marginBottom: 6 }}>x</div>
                        <BufferedNumberInput
                          style={{ width: 92 }}
                          value={lift.x}
                          fallback={0}
                          onCommit={(value) => {
                            onManagedLiftChange(lift.id, {
                              ...lift,
                              x: value,
                            });
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ marginBottom: 6 }}>y</div>
                        <BufferedNumberInput
                          style={{ width: 92 }}
                          value={lift.y}
                          fallback={0}
                          onCommit={(value) => {
                            onManagedLiftChange(lift.id, {
                              ...lift,
                              y: value,
                            });
                          }}
                        />
                      </div>
                    </Space>

                    <Space size="small" style={{ width: "100%", marginTop: 8 }}>
                      <div>
                        <div style={{ marginBottom: 6 }}>Width (px)</div>
                        <BufferedNumberInput
                          min={MIN_SHAPE_SIZE}
                          value={lift.backgroundWidth}
                          fallback={MIN_SHAPE_SIZE}
                          onCommit={(value) => {
                            if (isLinkedBankLocked(lift.linkedBank)) {
                              onManagedLiftChange(lift.id, {
                                ...lift,
                                ...getLiftSizeLockedToWidth(lift, value),
                              });
                              return;
                            }

                            onManagedLiftChange(lift.id, {
                              ...lift,
                              backgroundWidth: value,
                            });
                          }}
                          style={{ width: 110 }}
                        />
                      </div>
                      <div>
                        <div style={{ marginBottom: 6 }}>Height (px)</div>
                        <BufferedNumberInput
                          min={MIN_SHAPE_SIZE}
                          value={lift.backgroundHeight}
                          fallback={MIN_SHAPE_SIZE}
                          onCommit={(value) => {
                            if (isLinkedBankLocked(lift.linkedBank)) {
                              onManagedLiftChange(lift.id, {
                                ...lift,
                                ...getLiftSizeLockedToHeight(lift, value),
                              });
                              return;
                            }

                            onManagedLiftChange(lift.id, {
                              ...lift,
                              backgroundHeight: value,
                            });
                          }}
                          style={{ width: 110 }}
                        />
                      </div>
                      <div>
                        <div style={{ marginBottom: 6 }}>Background</div>
                        <input
                          type="color"
                          value={lift.backgroundColorHex}
                          onChange={(event) => {
                            onManagedLiftChange(lift.id, {
                              ...lift,
                              backgroundColorHex: event.target.value,
                            });
                          }}
                          style={{
                            width: 52,
                            height: 24,
                            border: "none",
                            padding: 0,
                            background: "none",
                          }}
                        />
                      </div>
                    </Space>

                    <div style={{ marginTop: 8 }}>
                      <Checkbox
                        checked={lift.displayName}
                        onChange={(event) => {
                          onManagedLiftChange(lift.id, {
                            ...lift,
                            displayName: event.target.checked,
                          });
                        }}
                      >
                        Display Name
                      </Checkbox>
                    </div>
                  </div>
                ))}
              </Space>
            </div>
          )}
        </Space>
      </div>
    </Drawer>
  );
}

export default GroupEditorDrawer;
